import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Leaf, Ghost, ShieldQuestion, Download, Check, Loader2, Star, Contrast, MoonStar, SunMedium, Sprout } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* SpecStore — the phone's app store. Nothing shady comes preinstalled: GreenPages and
   Ghostwire have to be found here and downloaded for bank money. Dealer 101 sends
   players here after their first harvest. CivicOS themes live here too. */

const ICONS = { greenpages: Leaf, ghostwire: Ghost, vpnpro: ShieldQuestion, theme_contrast: Contrast, theme_stealth: MoonStar, theme_light: SunMedium, growtrack: Sprout };
const COLORS = { greenpages: "#4ade80", ghostwire: "#38bdf8", vpnpro: "#a1a1aa", theme_contrast: "#facc15", theme_stealth: "#818cf8", theme_light: "#fbbf24", growtrack: "#a3e635" };
const META = {
  greenpages: { rating: 4.6, dls: "12.4K", size: "8.2 MB" },
  ghostwire: { rating: 4.9, dls: "88.1K", size: "14.6 MB" },
  vpnpro: { rating: 2.1, dls: "640", size: "31 MB" },
  theme_contrast: { rating: 4.8, dls: "3.1K", size: "0.4 MB" },
  theme_stealth: { rating: 4.7, dls: "9.7K", size: "0.6 MB" },
  theme_light: { rating: 3.9, dls: "2.2K", size: "0.5 MB" },
  growtrack: { rating: 4.5, dls: "4.8K", size: "3.1 MB" },
};

export const STORE_DEMO = [
  { id: "greenpages", name: "GreenPages", cost: 0, tag: "Social · 17+", desc: "Free anon. weed network — menus, ratings, drops near you.", installed: false },
  { id: "ghostwire", name: "Ghostwire", cost: 250, tag: "Messaging", desc: "Encrypted chats. No tap, no trace. The one the plugs use.", installed: false },
  { id: "vpnpro", name: "VPN Pro+ Totally Legit", cost: 99, tag: "Utilities", desc: "Military-grade vibes. Definitely does something.", installed: false },
  { id: "theme_contrast", name: "High-Contrast Theme", cost: 0, tag: "CivicOS Theme", desc: "Stark black-and-white CivicOS skin. Free — accessibility is a right, not a purchase.", installed: false },
  { id: "theme_stealth", name: "Stealth Dark Theme", cost: 150, tag: "CivicOS Theme", desc: "Deep-black, low-glow CivicOS skin. For citizens who don't like being seen.", installed: false },
  { id: "theme_light", name: "Light Mode Theme", cost: 300, tag: "CivicOS Theme", desc: "Blindingly bright CivicOS skin. Braver than most criminals.", installed: false },
  { id: "growtrack", name: "GrowTrack", cost: 180, tag: "Grow Ops", desc: "Remote telemetry for your home grow — plant health, thirst alerts and market pulse from anywhere in the city.", installed: false },
];

export default function SpecStoreApp({ demo, serverId, token, accent, installed, onInstalled, onOpen }) {
  const [catalog, setCatalog] = useState(demo ? STORE_DEMO : []);
  const [installing, setInstalling] = useState("");
  const [pct, setPct] = useState(0);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/leaf/store`, { params: { token } })
      .then((r) => setCatalog(r.data.catalog)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  // live catalog reflects server-side install state; demo mirrors via installed prop
  const isInstalled = (id) => catalog.find((c) => c.id === id)?.installed || installed?.includes(id);

  const install = (app) => {
    if (installing || isInstalled(app.id)) return;
    setInstalling(app.id);
    setPct(0);
    const iv = setInterval(() => setPct((p) => Math.min(96, p + Math.random() * 14)), 180);
    const finish = async () => {
      clearInterval(iv);
      if (demo) {
        setPct(100);
        toast.success(`${app.name} installed (demo)`);
      } else {
        try {
          const r = await api.post(`/nc/servers/${serverId}/leaf/store/buy`, { app: app.id }, { params: { token } });
          setPct(100);
          toast.success(r.data.message);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
          setInstalling(""); setPct(0);
          return;
        }
      }
      setTimeout(() => {
        setInstalling(""); setPct(0);
        setCatalog((c) => c.map((x) => (x.id === app.id ? { ...x, installed: true } : x)));
        onInstalled?.(app.id);
        if (!demo) load();
      }, 450);
    };
    setTimeout(finish, 1600);
  };

  return (
    <div data-testid="store-app" className="p-3 flex flex-col gap-2.5">
      <div className="flex items-center gap-2 px-1">
        <Download className="w-4 h-4" style={{ color: accent }} />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">SpecStore</p>
        <p className="ml-auto font-mono text-[8px] text-white/40 uppercase tracking-[0.15em]">featured</p>
      </div>
      {catalog.map((app) => {
        const Icon = ICONS[app.id] || ShieldQuestion;
        const meta = META[app.id] || {};
        const inst = isInstalled(app.id);
        return (
          <div key={app.id} data-testid={`store-app-${app.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${COLORS[app.id]}22`, border: `1px solid ${COLORS[app.id]}44` }}>
                <Icon className="w-5 h-5" style={{ color: COLORS[app.id] }} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-white">{app.name}</p>
                <p className="font-mono text-[8px] text-white/40 uppercase tracking-[0.1em]">{app.tag} · {meta.size}</p>
                <p className="font-mono text-[8px] text-amber-300/90 flex items-center gap-1 mt-0.5">
                  <Star className="w-2.5 h-2.5 fill-amber-300" />{meta.rating} · {meta.dls} downloads
                </p>
              </div>
              {inst ? (
                <button data-testid={`store-open-${app.id}`} onClick={() => onOpen?.(app.id)} disabled={app.id === "vpnpro"}
                  className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-300 border border-emerald-400/40 px-2.5 py-1.5 rounded-full disabled:opacity-60">
                  <Check className="w-3 h-3" /> {app.id === "vpnpro" ? "Installed" : "Open"}
                </button>
              ) : installing === app.id ? (
                <div className="w-20">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} /></div>
                  <p className="font-mono text-[7px] text-white/50 mt-1 text-center">{Math.round(pct)}%</p>
                </div>
              ) : (
                <button data-testid={`store-install-${app.id}`} onClick={() => install(app)}
                  className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3.5 py-2 rounded-full text-black transition-transform active:scale-95"
                  style={{ backgroundColor: app.cost ? "#f59e0b" : "#4ade80" }}>
                  {app.cost ? `$${app.cost}` : "Get"}
                </button>
              )}
            </div>
            <p className="font-mono text-[9px] text-white/50 mt-2 leading-relaxed">{app.desc}</p>
          </div>
        );
      })}
      <p className="font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em] mt-1">SpecStore review board approved · Bornwood region</p>
    </div>
  );
}
